import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { FileDropzone } from '../../../../../components/ui/FileDropzone';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { uploadReceipts } from '../../../../../features/receipts/receiptsThunks';
import {
  RECEIPTS_TEMPLATE_COLUMNS,
  RECEIPTS_TEMPLATE_EXAMPLE,
} from '../../../../../features/receipts/receiptsConstants';

/** The server reads the sheet with openpyxl, which does not open the old `.xls`. */
const SHEET_EXTENSIONS = ['.xlsx'];

const downloadTemplate = () => {
  const sheet = XLSX.utils.json_to_sheet(RECEIPTS_TEMPLATE_EXAMPLE, {
    header: RECEIPTS_TEMPLATE_COLUMNS,
  });
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, 'Recepciones');
  XLSX.writeFile(book, 'plantilla-recepciones.xlsx');
};

/**
 * Declares several receiving orders out of one sheet, for ONE client in the
 * warehouse in use. One row per SKU; the `Referencia` column groups rows into
 * an order.
 *
 * It is still declaring: nothing enters the stock until each order is
 * received. And it is all or nothing, which the dialog says before anything is
 * sent.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object|null} props.warehouse - Where the loads will arrive.
 * @param {Array<{value: number, label: string}>} props.clientOptions
 * @param {(message: string) => void} props.onDone - Told the server's own summary.
 */
export const ReceiptsUploadModal = ({ isOpen, onClose, warehouse, clientOptions, onDone }) => {
  const dispatch = useDispatch();
  const uploading = useSelector((state) => state.receipts.uploading);

  const [companyId, setCompanyId] = useState(null);
  const [file, setFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  const onlyClientId = clientOptions.length === 1 ? clientOptions[0].value : null;

  // Every opening starts without a file.
  useEffect(() => {
    if (!isOpen) return;
    setCompanyId(onlyClientId);
    setFile(null);
    setUploadError(null);
  }, [isOpen, onlyClientId]);

  const problem = (() => {
    if (!companyId) return 'Elige el cliente';
    if (!file) return 'Elige la planilla';
    return null;
  })();

  const handleUpload = async () => {
    setUploadError(null);
    const action = await dispatch(
      uploadReceipts({ warehouseId: warehouse.id, companyId, file })
    );

    if (action.meta.requestStatus !== 'fulfilled') {
      // Nothing was created. The sheet stays staged so it is clear which one failed.
      setUploadError(action.payload);
      return;
    }

    onDone(action.payload.message);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Carga masiva de recepciones"
      testId="receipts-upload-modal"
    >
      <p className="text-sm text-text-secondary text-center -mt-4 mb-6">
        {warehouse?.name} · una fila por SKU; la columna Referencia agrupa las filas en una orden
      </p>

      <div className="space-y-4">
        {uploadError && (
          <div
            data-testid="receipts-upload-error"
            className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm"
          >
            {uploadError}
          </div>
        )}

        {clientOptions.length > 1 && (
          <div>
            <FilterableSelect
              variant="form"
              testId="receipts-upload-client"
              label="Cliente"
              value={companyId}
              onChange={setCompanyId}
              options={clientOptions}
              placeholder="Seleccionar..."
              searchPlaceholder="Buscar cliente..."
              disabled={uploading}
            />
            <p className="text-text-muted text-xs mt-1">
              Todos los SKU de la planilla tienen que ser de esta empresa.
            </p>
          </div>
        )}

        <FileDropzone
          file={file}
          onFileSelected={(selected) => {
            setFile(selected);
            setUploadError(null);
          }}
          onClear={() => setFile(null)}
          extensions={SHEET_EXTENSIONS}
          hint="Planilla .xlsx con las columnas Referencia, SKU y Cantidad"
          disabled={uploading}
        />

        <div className="text-xs text-text-muted space-y-1">
          <p>
            Columnas: <strong>Referencia</strong>, <strong>SKU</strong> y{' '}
            <strong>Cantidad</strong> obligatorias. Lote, Vence, Proveedor y Notas opcionales;
            proveedor y notas se toman de la primera fila de cada referencia.
          </p>
          <p>
            Es todo o nada: si una fila falla, no se crea ninguna orden y el error dice cuál. Sigue
            siendo declarar: el stock entra recién al recepcionar cada orden, y ahí se elige la
            ubicación.
          </p>
        </div>

        <Button
          variant="ghost"
          size="sm"
          icon={Download}
          onClick={downloadTemplate}
          data-testid="receipts-upload-template"
        >
          Descargar plantilla
        </Button>
      </div>

      <div className="flex justify-end gap-3 mt-8">
        <Button variant="ghost" onClick={onClose} disabled={uploading}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          loading={uploading}
          loadingLabel="Cargando..."
          disabled={Boolean(problem)}
          onClick={handleUpload}
          data-testid="receipts-upload-submit"
        >
          {problem || 'Cargar planilla'}
        </Button>
      </div>
    </Modal>
  );
};
